import * as yup from 'yup';
import { Local } from '@/entities/auth/local.enum';
import { UserRole } from '@/entities/auth/user-role.enum';
import { Gender } from '@/entities/auth/gender.enum';
import {
  AcquisitionSource,
  CompanyInvoicingType,
  IntendedRentalFrequency,
  PaymentTermDayType,
  VehicleClassPreference,
} from '@carvia/ros-client-types';
import { isNil } from 'lodash';
import { vatIdSchema } from './vat-id.schema';

type Schema = yup.StringSchema<string | undefined, yup.AnyObject, undefined, ''>;

const isNotCustomerThanOptional = {
  is: (role: UserRole) => role === UserRole.CUSTOMER || role === UserRole.AGENCY,
  then: (schema: Schema) => schema.required(),
  otherwise: (schema: Schema) => schema.optional().nullable(),
};

const optionalNumber = yup
  .number()
  .transform((value) =>
    value === '' || value === null || value === undefined || isNaN(Number(value))
      ? undefined
      : Number(value),
  )
  .optional();

export const userEditSchema = yup.object({
  company: yup.string().optional().nullable(),
  gender: yup.string().oneOf(Object.values(Gender)).required(),
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  email: yup.string().required().email(),
  password: yup.string().min(8).optional(),
  role: yup.string().oneOf(Object.values(UserRole)).optional(),
  partnerSubTypeId: yup.string().when('role', {
    is: (role: UserRole) => role === UserRole.AGENCY || role === UserRole.COMPANY,
    then: (schema: Schema) => schema.required(),
    otherwise: (schema: Schema) => schema.optional().nullable(),
  }),
  phone: yup.string().optional().nullable(),
  sipId: yup.string().optional().nullable(),
  did: yup.string().optional().nullable(),
  callerId: yup.string().optional().nullable(),
  staffomaticUserId: yup.string().optional().nullable(),
  street: yup.string().when('role', isNotCustomerThanOptional),
  zip: yup.string().when('role', isNotCustomerThanOptional),
  city: yup.string().when('role', isNotCustomerThanOptional),
  country: yup.string().when('role', isNotCustomerThanOptional),
  vatId: vatIdSchema().optional().nullable(),
  paymentTermDays: yup.number().integer().min(0).max(365).optional().nullable(),
  paymentTermDayType: yup
    .mixed<PaymentTermDayType>()
    .oneOf(Object.values(PaymentTermDayType))
    .optional()
    .nullable(),
  dateOfBirth: yup.date().optional().nullable(),
  stationIds: yup.array(yup.string().required()),
  preferredLocal: yup.string().oneOf(Object.values(Local)).required(),
  preferredPaymentTerminalId: yup.string().optional().nullable(),
  licenseNumber: yup.string().optional().nullable(),
  licenseDate: yup.string().optional().nullable(),
  licenseAuthority: yup.string().optional().nullable(),
  idCardNumber: yup.string().optional().nullable(),
  idCardDate: yup.date().optional().nullable(),
  idCardAuthority: yup.string().optional().nullable(),
  comment: yup.string().optional().nullable(),
  functions: yup.array(yup.string().required()).optional(),
  agencySettings: yup
    .object({
      acquisitionSource: yup
        .mixed<AcquisitionSource>()
        .oneOf(Object.values(AcquisitionSource))
        .optional()
        .nullable(),
      intendedRentalFrequency: yup
        .mixed<IntendedRentalFrequency>()
        .oneOf(Object.values(IntendedRentalFrequency))
        .optional()
        .nullable(),
      requiredVehicleClasses: yup
        .array(
          yup
            .mixed<VehicleClassPreference>()
            .oneOf(Object.values(VehicleClassPreference))
            .required(),
        )
        .optional()
        .nullable(),
      discountPercent: optionalNumber,
      commissionPercent: optionalNumber,
      invoiceEmail: yup.string().email().optional(),
      contactPerson: yup.string().optional(),
      allowedEmployeeDomains: yup.array(yup.string().required()).optional(),
      companyInvoicingType: yup
        .mixed<CompanyInvoicingType>()
        .oneOf(Object.values(CompanyInvoicingType))
        .optional(),
      noDeposit: yup
        .boolean()
        .transform((value) => (isNil(value) ? undefined : value))
        .optional(),
    })
    .optional()
    .nullable(),
});
